<template>
  <SharedCMHorizontalScrollingBase
    ref="sharedBaseComponent"
    v-editable="blok"
    class="cm-horizontal-scrolling"
    :class="blok.is_dark ? 'is-dark' : 'is-light'"
    :progress="state.progress"
    :sizeMap="{
      square: [5, 4, 3],
      portrait: [4, 4, 3],
      landscape: [6, 6, 4],
    }">
    <!-- Head -->
    <template #head>
      <template v-for="(item, index) in blok.head" :key="index">
        <ItemContentHead
          v-if="item.component === 'item_content-head'"
          :blok="item"
          :isDark="blok.is_dark"
          :headSize="blok.head_size"
          class="head-mobile" />
        <ItemQuote
          v-else-if="item.component === 'item_quote'"
          :blok="{
            _uid: item._uid,
            component: item.component,
            text: item.text,
            layout: 'full-size',
          }"
          :isDark="blok.is_dark"
          :isLeftIndented="false"
          class="head-mobile" />
      </template>
    </template>

    <template #cards>
      <!-- Head card -->
      <div
        v-if="blok.head?.length && !isMobile"
        class="card is-head"
        :style="{
          width: isHeadStatement || isHeadSmall ? `calc(425px + ${$sharedBaseComponent?.getWidth(2)})` : '565px',
          paddingLeft: isHeadStatement || isHeadSmall ? $sharedBaseComponent?.getWidth(2) : '0',
        }">
        <ItemContentHead
          v-if="blok.head[0]?.component === 'item_content-head'"
          :blok="blok.head[0]"
          :isDark="blok.is_dark"
          :headSize="blok.head_size"
          class="card-inner-head" />
        <ItemQuote
          v-else-if="blok.head[0]?.component === 'item_quote'"
          :blok="{
            _uid: blok.head[0]._uid,
            component: blok.head[0].component,
            text: blok.head[0].text,
            layout: 'full-size',
          }"
          :isDark="blok.is_dark"
          :isLeftIndented="false" />
      </div>

      <template v-for="(cluster, clusterIndex) in blok.items" :key="clusterIndex">
        <!-- Space -->
        <div
          v-if="!(!clusterIndex && isMobile)"
          class="card is-item is-spacer"
          :style="{ width: $sharedBaseComponent?.getWidth(1) }"></div>

        <!-- Card -->
        <template v-for="(card, cardIndex) in [cluster.card_1, cluster.card_2, cluster.card_3]" :key="cardIndex">
          <div
            v-if="card?.length"
            class="card is-item"
            :style="{
              alignSelf: [
                cluster.card_1_alignment === 'bottom' ? 'flex-end' : 'flex-start',
                cluster.card_2_alignment === 'bottom' ? 'flex-end' : 'flex-start',
                cluster.card_3_alignment === 'bottom' ? 'flex-end' : 'flex-start',
              ][cardIndex],
              width: getCardWidth(clusterIndex, cardIndex),
            }">
            <ItemCardContent
              :blok="card[0]"
              class="card-item-card"
              :breakpointsWidthMap="{ '0': getImageWidth(getCardWidth(clusterIndex, cardIndex)) }" />
          </div>
        </template>
      </template>
    </template>
  </SharedCMHorizontalScrollingBase>
</template>

<script lang="ts" setup>
import { computed, useTemplateRef } from 'vue';
import { SharedCMHorizontalScrollingBase } from '@seb-platform/shared/components/storyblok';
import { useBreakpoint } from '@seb-platform/shared/composables';
import ItemCardContent from '~/components/storyblok/item/ItemCardContent/ItemCardContent.vue';
import ItemContentHead from '~/components/storyblok/item/ItemContentHead/ItemContentHead.vue';
import ItemQuote from '~/components/storyblok/item/ItemQuote/ItemQuote.vue';
import type { CmHorizontalScrollingStoryblok } from '~/types/storyblok-generated';
import type { ModuleWrapperState } from '~/types/utils';

const props = defineProps<{
  blok: CmHorizontalScrollingStoryblok;
  state: ModuleWrapperState;
}>();

const { state: stateBreakpoint } = useBreakpoint();

const $sharedBaseComponent = useTemplateRef('sharedBaseComponent');
const isHeadSmall = computed(() => props.blok.head_size === 'small');
const isHeadStatement = computed(() => props.blok.head?.[0]?.component === 'item_quote');
const isMobile = computed(() => !stateBreakpoint.isPhoneLarge);

/**
 * Gets the actual with of the card in pixels.
 */
function getCardWidth(clusterIndex: number, itemIndex: number) {
  if (!$sharedBaseComponent.value?.getCardWidth || !props.blok.items) {
    return '0px';
  }

  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  return $sharedBaseComponent.value.getCardWidth(props.blok.items, clusterIndex, itemIndex);
}

/**
 * We don't want to create lots of assets, so we limit images sizes to steps of 20
 */
function getImageWidth(cardWidth: string) {
  const parsedCardWidth = parseInt(cardWidth.replace('px', ''));
  return Math.ceil(parsedCardWidth / 20) * 20;
}
</script>

<style src="./CMHorizontalScrolling.scss" lang="scss" scoped />
